import type { Patient, Reference } from '@medplum/fhirtypes'
import * as codeSystems from '@seen/core/codeSystems'
import type { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { Signature } from '@seen/core/models/signature'
import { getReferenceString } from '@seen/core/references'
import { useMemo } from 'react'
import { useSearchResourcePages } from '#src/hooks/useSearchResourcePages.ts'

/*
 * Hook to determine which documents have been signed by ANY practitioner.
 * Returns a Map of document reference strings to Signature objects.
 * Queries signatures by participant to avoid URL length limits with many documents.
 *
 * Note: For per-practitioner signature checking, use ExtendedDocumentReference.hasPractitionerSigned().
 */
export function useDocumentSignatureStatus(
  documents: ExtendedDocumentReference[]
): Map<string, Signature> {
  // Get participant from documents (they should all have the same subject)
  const participant = useMemo<Reference<Patient> | undefined>(() => {
    const firstDocWithSubject = documents.find((d) => d.subject)
    return firstDocWithSubject?.subject
  }, [documents])

  // Build set of signable document IDs for filtering
  const signableDocumentIds = useMemo(
    () => new Set(documents.flatMap((d) => (d.subject ? [d.id] : []))),
    [documents]
  )

  // Query signatures by participant via chained search (avoids URL length issues)
  // Uses useSearchResourcePages to handle pagination for participants with many signatures
  const { data: allParticipantSignatures = [] } = useSearchResourcePages(
    Signature,
    participant
      ? {
          'target:DocumentReference.subject': participant,
          '_tag:not': `${codeSystems.SIGNATURE_VOIDED_TAG_SYSTEM}|`, // exclude voided signatures
          _sort: '-_lastUpdated',
        }
      : undefined,
    { query: { enabled: !!participant } }
  )

  // Filter to only signatures for documents in our list
  // Keep first signature per document (most recent due to -_lastUpdated sort)
  return useMemo(() => {
    const signaturesByDoc = new Map<string, Signature>()
    for (const sig of allParticipantSignatures) {
      const targetRef = getReferenceString(sig.target)
      const docId = sig.target?.reference?.split('/')[1]
      if (
        docId &&
        signableDocumentIds.has(docId) &&
        !signaturesByDoc.has(targetRef)
      ) {
        signaturesByDoc.set(targetRef, sig)
      }
    }
    return signaturesByDoc
  }, [allParticipantSignatures, signableDocumentIds])
}
